import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  connect() {
    this.fetchDataAndRender()
    this.activeCategories = ['Total Active', 'Total Programmed', 'Other']
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async fetchDataAndRender() {
    try {
      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search)

      // Build the fetch URL with the same parameters
      const fetchUrl = `/trends/source_of_streams?${urlParams.toString()}`

      const response = await fetch(fetchUrl)
      this.chartData = await response.json()

      // Update totals in the UI
      this.updateTotals()

      this.renderChart(this.chartData)
    } catch (error) {
      console.error('Error fetching source of streams chart data:', error)
      this.showError()
    }
  }

  updateTotals() {
    const totalActiveEl = document.getElementById('total-active-value')
    const totalProgrammedEl = document.getElementById('total-programmed-value')
    const otherEl = document.getElementById('other-value')
    const dateLabel = this.chartData.date_range_label || 'Last 7 Days'

    // Update main category totals
    if (totalActiveEl) {
      totalActiveEl.innerHTML = `<div>${dateLabel}</div><div class="font-semibold text-gray-900">${this.chartData.total_active.toLocaleString()}</div>`
    }
    if (totalProgrammedEl) {
      totalProgrammedEl.innerHTML = `<div>${dateLabel}</div><div class="font-semibold text-gray-900">${this.chartData.total_programmed.toLocaleString()}</div>`
    }
    if (otherEl) {
      otherEl.innerHTML = `<div>${dateLabel}</div><div class="font-semibold text-gray-900">${this.chartData.total_other.toLocaleString()}</div>`
    }

    // Update individual source totals (skip "Other" as it's handled above as a main category)
    const totals = this.chartData.totals || {}
    Object.keys(totals).forEach(sourceName => {
      // Skip "Other" because it's already updated as a main category total above
      if (sourceName === 'Other') return

      const elementId = sourceName.toLowerCase().replace(/ /g, '_') + '-value'
      const element = document.getElementById(elementId)
      if (element) {
        element.textContent = totals[sourceName].toLocaleString()
      }
    })
  }

  toggleSeries(sourceName, isVisible) {
    if (!this.chart) return

    // Define source mappings
    const activeSources = ['Album', 'Artist', 'Collection', 'Search', 'Play Queue']
    const programmedSources = ['Editorial Playlist', 'User Playlist', 'Radio', 'Chart', 'Algorithmic Playlist', 'Your DJ']

    let seriesToToggle = []

    if (sourceName === 'Total Active') {
      seriesToToggle = activeSources
    } else if (sourceName === 'Total Programmed') {
      seriesToToggle = programmedSources
    } else if (sourceName === 'Other') {
      seriesToToggle = ['Other']
    } else {
      // Individual source toggle
      seriesToToggle = [sourceName]
    }

    // Toggle each series
    seriesToToggle.forEach(seriesName => {
      const seriesIndex = this.chartData.series.findIndex(s => s.name === seriesName)
      if (seriesIndex !== -1) {
        if (isVisible) {
          this.chart.showSeries(seriesName)
        } else {
          this.chart.hideSeries(seriesName)
        }
      }
    })
  }

  renderChart(data) {
    if (!data || !data.series || data.series.length === 0) {
      this.showEmptyState()
      return
    }

    // Clear the loading message and any existing content
    this.element.innerHTML = ''

    const options = {
      series: data.series,
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd'
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            return Math.floor(value).toLocaleString()
          }
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          // Get the date string directly from the data to avoid timezone issues
          const dateTimestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
          const date = new Date(dateTimestamp)

          // Format date using UTC to avoid timezone offset issues
          const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })

          let tooltipHtml = '<div class="px-3 py-2" style="min-width: 200px;">'
          tooltipHtml += `<div class="font-semibold text-gray-900 mb-2">${formattedDate}</div>`

          // Build rows for each source
          let sourceRows = []
          w.globals.seriesNames.forEach((name, index) => {
            const value = series[index][dataPointIndex]
            if (value !== undefined && value !== null) {
              const color = w.globals.colors[index]
              sourceRows.push({
                name: name,
                value: value,
                color: color
              })
            }
          })

          // Sort by value descending
          sourceRows.sort((a, b) => b.value - a.value)

          // Add each source row
          sourceRows.forEach(row => {
            tooltipHtml += '<div class="flex items-center justify-between gap-4 py-1">'
            tooltipHtml += `<div class="flex items-center gap-2">`
            tooltipHtml += `<div class="w-3 h-3 rounded-sm" style="background-color: ${row.color};"></div>`
            tooltipHtml += `<span class="text-sm text-gray-700">${row.name}</span>`
            tooltipHtml += `</div>`
            tooltipHtml += `<span class="text-sm font-medium text-gray-900">${row.value.toLocaleString()}</span>`
            tooltipHtml += '</div>'
          })

          tooltipHtml += '</div>'
          return tooltipHtml
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 10
      },
      colors: data.colors || ['#cab2d6', '#6a3d9a', '#a65628'],
      grid: {
        borderColor: '#434343',
        row: {
          colors: ['transparent', 'transparent'],
          opacity: 0.5
        }
      }
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  showEmptyState() {
    this.element.innerHTML = '<div class="text-center py-8 text-gray-500">No source of streams data available for the selected filters.</div>'
  }

  showError() {
    this.element.innerHTML = '<div class="text-center py-8 text-red-500">Error loading chart data. Please try again.</div>'
  }
}
